import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { 
  FaPhone, 
  FaEnvelope, 
  FaFacebook,
  FaLinkedin,
  FaInstagram,
  FaCheckCircle,
  FaInfoCircle,
  FaUserTie,
  FaBuilding
} from 'react-icons/fa';
import { 
  Card, 
  CardBody, 
  CardHeader, 
  Button, 
  Input, 
  Textarea, 
  Select, 
  SelectItem, 
  Tabs,
  Tab,
  Accordion,
  AccordionItem,
  Link,
  useDisclosure,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Checkbox
} from "@heroui/react";
import UnifiedContactCard from './UnifiedContactCard';

const Contact = () => {
  // Reference for animation trigger
  const contactRef = useRef(null);
  const isInView = useInView(contactRef, { once: true, amount: 0.1 });
  
  // Active tab state
  const [activeTab, setActiveTab] = useState("contact");
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
    budget: '',
    services: [],
    deadline: '',
    policy: false
  });
  
  // Form validation states
  const [errors, setErrors] = useState({});
  const [formTouched, setFormTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Success modal
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  // Handle form field change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Mark field as touched
    setFormTouched(prev => ({ ...prev, [name]: true }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  // Handle checkbox change
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
    
    // Mark field as touched
    setFormTouched(prev => ({ ...prev, [name]: true }));
    
    // Clear error when user checks
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  // Handle select change
  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Mark field as touched
    setFormTouched(prev => ({ ...prev, [name]: true }));
    
    // Clear error
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  // Handle multi-select change
  const handleMultiSelectChange = (name, values) => {
    setFormData(prev => ({ ...prev, [name]: values }));
    
    // Mark field as touched
    setFormTouched(prev => ({ ...prev, [name]: true }));
    
    // Clear error
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // Required fields validation
    if (!formData.name.trim()) {
      newErrors.name = 'Imię i nazwisko jest wymagane';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email jest wymagany';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Niepoprawny format adresu email';
    }
    
    if (!formData.subject) {
      newErrors.subject = 'Temat jest wymagany';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Wiadomość jest wymagana';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Wiadomość jest zbyt krótka (min. 10 znaków)';
    }
    
    if (!formData.policy) {
      newErrors.policy = 'Wymagana akceptacja polityki prywatności';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
// Zaktualizowana funkcja handleSubmit w komponencie Contact
const handleSubmit = async (e) => {
  e.preventDefault();
  
  // Mark all fields as touched
  const allTouched = {};
  Object.keys(formData).forEach(key => {
    allTouched[key] = true;
  });
  setFormTouched(allTouched);
  
  // Validate form
  if (!validateForm()) {
    return;
  }
  
  // Set submitting state
  setSubmitting(true);
  
  try {
    // Wywołanie API
    const response = await fetch('/api/contact', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData)
    });
    
    const data = await response.json();
    
    if (response.ok) {
      // Sukces
      setSubmitSuccess(true);
      onOpen(); // Open success modal
      
      // Reset form after successful submission
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: '',
        budget: '',
        services: [],
        deadline: '',
        policy: false
      });
      setFormTouched({});
    } else {
      // Błąd zwrócony przez API
      setErrors(prev => ({
        ...prev,
        form: data.detail || 'Wystąpił błąd podczas wysyłania formularza. Spróbuj ponownie.'
      }));
    }
  } catch (error) {
    // Błąd połączenia
    setErrors(prev => ({
      ...prev,
      form: 'Nie można połączyć się z serwerem. Sprawdź połączenie internetowe i spróbuj ponownie.'
    }));
    console.error('Error submitting form:', error);
  } finally {
    setSubmitting(false);
  }
};
  
  // FAQ data
  const faqItems = [
    {
      question: 'Jak wygląda proces współpracy?',
      answer: 'Proces współpracy zaczyna się od bezpłatnej konsultacji i analizy potrzeb. Następnie przygotowujemy indywidualną ofertę i plan działania. Po akceptacji przystępujemy do realizacji, informując na bieżąco o postępach. Po zakończeniu projektu zapewniamy wsparcie techniczne i możliwość rozwoju rozwiązania.'
    },
    {
      question: 'Jakie są terminy realizacji projektów?',
      answer: 'Terminy realizacji zależą od skali i złożoności projektu. Małe projekty realizujemy zwykle w ciągu 1-2 tygodni, średnie w ciągu 2-4 tygodni. Dokładny harmonogram ustalamy indywidualnie po szczegółowej analizie wymagań.'
    },
    {
      question: 'Czy oferujecie usługi wsparcia technicznego po zakończeniu projektu?',
      answer: 'Tak, oferujemy kompleksowe usługi wsparcia technicznego po zakończeniu projektu. Możemy pomóc w bieżącej obsłudze, aktualizacjach i rozwiązywaniu problemów. Oferujemy również pakiety wsparcia dopasowane do Twoich potrzeb.'
    },
    {
      question: 'Jakie są metody płatności?',
      answer: 'Akceptujemy przelewy bankowe i płatności rozłożone na etapy realizacji projektu. Dla stałych klientów oferujemy także abonamenty rozliczane miesięcznie. Szczegółowe warunki ustalamy indywidualnie w umowie.'
    },
    {
      question: 'Czy pracujecie z małymi firmami?',
      answer: 'Oczywiście! Specjalizujemy się w pracy z małymi i średnimi firmami. Rozumiemy, że mniejsze firmy mają inne potrzeby i budżety niż korporacje, dlatego nasze rozwiązania są zawsze dopasowane do skali i możliwości klienta.'
    }
  ];

  // Animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 50, damping: 17 }
    }
  };

  return (
    <div id="contact" className="py-20 bg-background relative overflow-hidden" ref={contactRef}>
      {/* Background decorative elements */}
      <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/10 rounded-full filter blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-secondary/10 rounded-full filter blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="text-primary font-medium text-sm tracking-wider uppercase mb-2 inline-block">Kontakt</span>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Skontaktuj się z <span className="text-primary">Nami</span>
          </h2>
          <p className="text-foreground-muted max-w-2xl mx-auto">
            Jesteśmy tutaj, aby odpowiedzieć na Twoje pytania i pomóc Ci w realizacji
            Twoich potrzeb IT. Skontaktuj się z nami w najwygodniejszy dla Ciebie sposób.
          </p>
        </motion.div>

        {/* Tabs navigation */}
        <div className="mb-12">
          <Tabs 
            aria-label="Opcje kontaktu"
            selectedKey={activeTab}
            onSelectionChange={setActiveTab}
            color="primary"
            variant="underlined"
            classNames={{
              base: "flex justify-center",
              tabList: "gap-4 md:gap-8",
              cursor: "bg-primary",
              tab: "max-w-fit px-2 h-10 data-[selected=true]:font-semibold"
            }}
          >
            <Tab key="contact" title="Formularz kontaktowy" />

            <Tab key="faq" title="FAQ" />
            {/*<Tab key="map" title="Mapa i dojazd" />*/}
          </Tabs>
        </div>

        {/* Tab content */}
        <div>
          {/* Contact Form Tab */}
          {activeTab === "contact" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact form */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Card className="shadow-xl" isBlurred>
                  <CardHeader>
                    <h3 className="text-2xl font-bold text-primary">
                      Wyślij nam wiadomość
                    </h3>
                  </CardHeader>
                  <CardBody>
                    <form onSubmit={handleSubmit}>
                      <div className="space-y-6">
                        {/* Personal information */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <Input
                              type="text"
                              label="Imię i nazwisko"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              variant="bordered"
                              color={errors.name && formTouched.name ? "danger" : "primary"}
                              isRequired
                              fullWidth
                              errorMessage={formTouched.name ? errors.name : ""}
                              startContent={
                                <FaUserTie className="text-default-400 text-sm" />
                              }
                            />
                          </div>
                          <div>
                            <Input
                              type="email"
                              label="Email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              variant="bordered"
                              color={errors.email && formTouched.email ? "danger" : "primary"}
                              isRequired
                              fullWidth
                              errorMessage={formTouched.email ? errors.email : ""}
                              startContent={
                                <FaEnvelope className="text-default-400 text-sm" />
                              }
                            />
                          </div>
                          <div>
                            <Input
                              type="tel"
                              label="Telefon"
                              name="phone"
                              value={formData.phone}
                              onChange={handleChange}
                              variant="bordered"
                              color="primary"
                              fullWidth
                              startContent={
                                <FaPhone className="text-default-400 text-sm" />
                              }
                            />
                          </div>
                          <div>
                            <Input
                              type="text"
                              label="Firma (opcjonalnie)"
                              name="company"
                              value={formData.company}
                              onChange={handleChange}
                              variant="bordered"
                              color="primary"
                              fullWidth
                              startContent={
                                <FaBuilding className="text-default-400 text-sm" />
                              }
                            />
                          </div>
                        </div>
                        
                        {/* Project information */}
                        <div className="space-y-6">
                          <Select
                            label="Temat"
                            selectedKeys={formData.subject ? [formData.subject] : []}
                            onChange={(e) => handleSelectChange('subject', e.target.value)}
                            variant="bordered"
                            color={errors.subject && formTouched.subject ? "danger" : "primary"}
                            isRequired
                            fullWidth
                            errorMessage={formTouched.subject ? errors.subject : ""}
                          >
                            <SelectItem key="Konsultacja" value="Konsultacja">Konsultacja</SelectItem>
                            <SelectItem key="Wsparcie techniczne" value="Wsparcie techniczne">Wsparcie techniczne</SelectItem>
                            <SelectItem key="Zapytanie ofertowe" value="Zapytanie ofertowe">Zapytanie ofertowe</SelectItem>
                            <SelectItem key="Współpraca" value="Współpraca">Współpraca</SelectItem>
                            <SelectItem key="Inne" value="Inne">Inne</SelectItem>
                          </Select>
                          
                          <Textarea
                            label="Wiadomość"
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            variant="bordered"
                            color={errors.message && formTouched.message ? "danger" : "primary"}
                            rows={4}
                            isRequired
                            fullWidth
                            errorMessage={formTouched.message ? errors.message : ""}
                            placeholder="Opisz swoje potrzeby lub zadaj pytanie..."
                          />
                        </div>
                        
                        {/* Additional information - shown conditionally */}
                        {(formData.subject === 'Zapytanie ofertowe' || formData.subject === 'Współpraca') && (
                          <div className="space-y-6 border border-divider rounded-lg p-4 bg-default-50">
                            <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                              <FaInfoCircle className="text-primary" />
                              Dodatkowe informacje (opcjonalnie)
                            </h4>
                            
                            <Select
                              label="Szacowany budżet"
                              selectedKeys={formData.budget ? [formData.budget] : []}
                              onChange={(e) => handleSelectChange('budget', e.target.value)}
                              variant="bordered"
                              color="primary"
                              fullWidth
                            >
                              <SelectItem key="do 2 000 PLN" value="do 2 000 PLN">do 2 000 PLN</SelectItem>
                              <SelectItem key="2 000 - 5 000 PLN" value="2 000 - 5 000 PLN">2 000 - 5 000 PLN</SelectItem>
                              <SelectItem key="5 000 - 10 000 PLN" value="5 000 - 10 000 PLN">5 000 - 10 000 PLN</SelectItem>
                              <SelectItem key="powyżej 10 000 PLN" value="powyżej 10 000 PLN">powyżej 10 000 PLN</SelectItem>
                              <SelectItem key="do ustalenia" value="do ustalenia">Do ustalenia</SelectItem>
                            </Select>
                            
                            <Select
                              label="Interesujące Cię usługi"
                              selectionMode="multiple"
                              selectedKeys={formData.services || []}
                              onChange={(e) => handleMultiSelectChange('services', e.target.value.split(','))}
                              variant="bordered"
                              color="primary"
                              fullWidth
                            >
                              <SelectItem key="Infrastruktura IT" value="Infrastruktura IT">Infrastruktura IT</SelectItem>
                              <SelectItem key="Bezpieczeństwo IT" value="Bezpieczeństwo IT">Bezpieczeństwo IT</SelectItem>
                              <SelectItem key="Strony i Aplikacje" value="Strony i Aplikacje">Strony i Aplikacje</SelectItem>
                              <SelectItem key="Rozwiązania Chmurowe" value="Rozwiązania Chmurowe">Rozwiązania Chmurowe</SelectItem>
                              <SelectItem key="Wsparcie IT" value="Wsparcie IT">Wsparcie IT</SelectItem>
                            </Select>
                            
                            <Input
                              type="text"
                              label="Preferowany termin realizacji"
                              name="deadline"
                              value={formData.deadline}
                              onChange={handleChange}
                              variant="bordered"
                              color="primary"
                              fullWidth
                              placeholder="np. 2 tygodnie, do końca miesiąca, itp."
                            />
                          </div>
                        )}
                        
                        {/* Privacy policy */}
                        <div>
                          <Checkbox 
                            name="policy"
                            isSelected={formData.policy}
                            onValueChange={(checked) => handleCheckboxChange({ target: { name: 'policy', checked }})}
                            color={errors.policy && formTouched.policy ? "danger" : "primary"}
                          >
                            <span className="text-sm">
                              Akceptuję <Link href="/privacy-policy" color="primary" underline="hover" isExternal>politykę prywatności</Link> i wyrażam zgodę na przetwarzanie moich danych osobowych
                            </span>
                          </Checkbox>
                          {errors.policy && formTouched.policy && (
                            <p className="text-danger text-xs mt-1">{errors.policy}</p>
                          )}
                        </div>
                        {errors.form && (
                          <div className="mt-4 p-3 bg-danger-50 border border-danger rounded-md">
                            <p className="text-danger text-sm flex items-center">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                              </svg>
                              {errors.form}
                            </p>
                          </div>
                        )}
                      </div>
                      
                      <div className="mt-8">
                        <Button
                          type="submit"
                          color="primary"
                          size="lg"
                          className="w-full"
                          isLoading={submitting}
                          endContent={!submitting && <FaEnvelope />}
                          disabled={submitting}
                        >
                          {submitting ? 'Wysyłanie...' : 'Wyślij wiadomość'}
                        </Button>
                        <p className="text-center text-xs text-foreground-muted mt-3">
                          Odpowiadamy zwykle w ciągu 24 godzin w dni robocze
                        </p>
                      </div>
                    </form>
                  </CardBody>
                </Card>
              </motion.div>

              {/* Contact information cards */}
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
              >
                <div className="mb-8">
                  <UnifiedContactCard />
                </div>

                {/* Map location preview 
                <motion.div variants={itemVariants}>
                  <Card className="shadow-xl overflow-hidden h-64 md:h-72 mb-6">
                    <CardBody className="p-0 relative">
                      <div className="w-full h-full bg-default-100 flex items-center justify-center overflow-hidden">
                        <iframe 
                          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d9771.810716389628!2d21.005154387446428!3d52.232938736889336!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x471ecc8c92692e49%3A0xc2e97ae5311f2dc2!2sPa%C5%82ac%20Kultury%20i%20Nauki!5e0!3m2!1spl!2spl!4v1621525123456!5m2!1spl!2spl" 
                          width="100%" 
                          height="100%" 
                          style={{ border: 0 }} 
                          allowFullScreen="" 
                          loading="lazy" 
                          referrerPolicy="no-referrer-when-downgrade"
                          title="Lokalizacja firmy IT"
                          className="filter grayscale brightness-90 mix-blend-luminosity hover:filter-none transition-all duration-500"
                        ></iframe>
                        
                        <div className="absolute bottom-4 right-4">
                          <Button 
                            as="a" 
                            href="https://goo.gl/maps/1234567890" 
                            target="_blank"
                            color="primary"
                            size="sm"
                            className="backdrop-blur-md bg-background/70"
                            endContent={<FaMapMarkerAlt />}
                          >
                            Pokaż na mapie
                          </Button>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </motion.div>*/}

                {/* Social media */}
                <motion.div variants={itemVariants} className="flex justify-center md:justify-start space-x-4">
                  <motion.a
                    href="#"
                    className="w-10 h-10 rounded-full bg-default-100 flex items-center justify-center text-primary"
                    whileHover={{ y: -5, scale: 1.1, backgroundColor: '#1877F2', color: '#FFFFFF' }}
                    whileTap={{ scale: 0.9 }}
                    aria-label="Facebook"
                  >
                    <FaFacebook />
                  </motion.a>
                  <motion.a
                    href="#"
                    className="w-10 h-10 rounded-full bg-default-100 flex items-center justify-center text-primary"
                    whileHover={{ y: -5, scale: 1.1, backgroundColor: '#0A66C2', color: '#FFFFFF' }}
                    whileTap={{ scale: 0.9 }}
                    aria-label="LinkedIn"
                  >
                    <FaLinkedin />
                  </motion.a>
                  <motion.a
                    href="#"
                    className="w-10 h-10 rounded-full bg-default-100 flex items-center justify-center text-primary"
                    whileHover={{ y: -5, scale: 1.1, backgroundColor: '#E4405F', color: '#FFFFFF' }}
                    whileTap={{ scale: 0.9 }}
                    aria-label="Instagram"
                  >
                    <FaInstagram />
                  </motion.a>
                </motion.div>
              </motion.div>
            </div>
          )}
          
          {/* FAQ Tab */}
          {activeTab === "faq" && (
            <div>
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-center mb-10"
              >
                <h3 className="text-2xl font-bold mb-4">Często zadawane pytania</h3>
                <p className="text-foreground-muted max-w-2xl mx-auto">
                  Znajdź odpowiedzi na najczęściej zadawane pytania dotyczące naszych usług,
                  procesu współpracy i wsparcia technicznego.
                </p>
              </motion.div>
              
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="max-w-3xl mx-auto"
              >
                <Accordion variant="splitted" className="space-y-4">
                  {faqItems.map((item, index) => (
                    <AccordionItem 
                      key={index} 
                      title={item.question} 
                      className="shadow-sm"
                      indicator={({ isOpen }) => (
                        isOpen ? (
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            className="h-4 w-4 text-primary" 
                            fill="none" 
                            viewBox="0 0 24 24" 
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        ) : (
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            className="h-4 w-4 text-primary" 
                            fill="none" 
                            viewBox="0 0 24 24" 
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )
                      )}
                    >
                      <p className="text-foreground-muted text-sm py-2">{item.answer}</p>
                    </AccordionItem>
                  ))}
                </Accordion>
              </motion.div>
              
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.5 }}
                className="mt-12 text-center"
              >
                <Card className="max-w-2xl mx-auto">
                  <CardBody className="py-6">
                    <h4 className="text-lg font-semibold mb-3">Nie znalazłeś odpowiedzi?</h4>
                    <p className="text-foreground-muted mb-4">
                      Zadaj nam pytanie, a odpowiemy najszybciej jak to możliwe.
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center gap-2">
                      <Button 
                        color="primary" 
                        onPress={() => setActiveTab("contact")}
                      >
                        Wyślij zapytanie
                      </Button>
                      <Button 
                        as="a"
                        href="tel:+48123456789"
                        variant="flat"
                        color="primary"
                        startContent={<FaPhone />}
                      >
                        Zadzwoń do nas
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              </motion.div>
            </div>
          )}
          
          {/* Map and Directions Tab 
          {activeTab === "map" && (
            <div>
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-center mb-10"
              >
                <h3 className="text-2xl font-bold mb-4">Jak do nas trafić</h3>
                <p className="text-foreground-muted max-w-2xl mx-auto">
                  Nasza siedziba znajduje się w centrum Warszawy, z dogodnym dojazdem
                  komunikacją miejską i samochodem.
                </p>
              </motion.div>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="lg:col-span-2"
                >
                  <Card className="shadow-xl overflow-hidden">
                    <CardBody className="p-0">
                      <div className="aspect-[16/9] lg:aspect-[4/3] 2xl:aspect-[16/9]">
                        <iframe 
                          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d9771.810716389628!2d21.005154387446428!3d52.232938736889336!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x471ecc8c92692e49%3A0xc2e97ae5311f2dc2!2sPa%C5%82ac%20Kultury%20i%20Nauki!5e0!3m2!1spl!2spl!4v1621525123456!5m2!1spl!2spl" 
                          width="100%" 
                          height="100%" 
                          style={{ border: 0 }} 
                          allowFullScreen="" 
                          loading="lazy" 
                          referrerPolicy="no-referrer-when-downgrade"
                          title="Lokalizacja firmy IT - mapa"
                        ></iframe>
                      </div>
                    </CardBody>
                  </Card>
                </motion.div>
                
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <Card className="shadow-lg">
                    <CardHeader>
                      <h4 className="text-xl font-semibold">Informacje dojazdu</h4>
                    </CardHeader>
                    <Divider />
                    <CardBody>
                      <motion.div variants={itemVariants} className="mb-6">
                        <h5 className="font-medium mb-2 flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          Adres
                        </h5>
                        <p className="text-foreground-muted ml-7">ul. Przykładowa 123</p>
                        <p className="text-foreground-muted ml-7">00-001 Warszawa</p>
                      </motion.div>
                      
                      <motion.div variants={itemVariants} className="mb-6">
                        <h5 className="font-medium mb-2 flex items-center">
                          <FaClock className="h-5 w-5 text-primary mr-2" />
                          Godziny pracy
                        </h5>
                        <p className="text-foreground-muted ml-7">Poniedziałek - Piątek: 9:00 - 17:00</p>
                      </motion.div>
                      
                      <motion.div variants={itemVariants} className="mb-6">
                        <h5 className="font-medium mb-2 flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-success mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          Komunikacja miejska
                        </h5>
                        <ul className="space-y-2 ml-7">
                          <li className="flex items-center">
                            <span className="text-sm">Metro: Stacja Centrum - 5 min pieszo</span>
                          </li>
                          <li className="flex items-center">
                            <span className="text-sm">Tramwaj: Linie 4, 15, 18, 35 - przystanek Centrum</span>
                          </li>
                          <li className="flex items-center">
                            <span className="text-sm">Autobus: Linie 131, 175, 501, 519, 522</span>
                          </li>
                        </ul>
                      </motion.div>
                      
                      <motion.div variants={itemVariants}>
                        <h5 className="font-medium mb-2 flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-danger mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6" />
                          </svg>
                          Parking
                        </h5>
                        <p className="text-foreground-muted ml-7 text-sm">
                          Publiczny parking płatny znajduje się przy ulicy Marszałkowskiej,
                          około 200m od biura.
                        </p>
                      </motion.div>
                    </CardBody>
                  </Card>
                  
                  <motion.div variants={itemVariants} className="mt-6">
                    <Button 
                      as="a" 
                      href="https://goo.gl/maps/1234567890" 
                      target="_blank"
                      rel="noopener noreferrer"
                      color="primary"
                      className="w-full"
                      endContent={<FaMapMarkerAlt />}
                    >
                      Nawiguj do nas
                    </Button>
                  </motion.div>
                </motion.div>
              </div>
            </div>
          )}*/}
        </div>
      </div>
      
      {/* Success modal */}
      <Modal 
        isOpen={isOpen} 
        onClose={onClose} 
        placement="center"
        classNames={{
          backdrop: "backdrop-blur-sm bg-black/50"
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1 text-center">
                <span className="text-success text-4xl flex justify-center mb-2">
                  <FaCheckCircle />
                </span>
                <h4 className="text-xl">Dziękujemy za wiadomość!</h4>
              </ModalHeader>
              <ModalBody>
                <p className="text-center">
                  Potwierdzamy otrzymanie Twojej wiadomości. 
                  Odpowiemy najszybciej jak to możliwe,
                  zwykle w ciągu 24 godzin w dni robocze.
                </p>
              </ModalBody>
              <ModalFooter className="justify-center">
                <Button color="primary" onPress={onClose}>
                  Rozumiem
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};

export default Contact;